/**
 * The training screen's arithmetic, with nothing attached to it.
 *
 * Everything here is a pure function of data the trainer already emits: chart
 * geometry, axis ranges, downsampling, the reward breakdown, the formatting of
 * a step count, and the validation of a checkpoint file somebody dropped in.
 * `src/ui/training.ts` turns the results into SVG and DOM; this file never
 * mentions either, which is what lets the numbers be tested numerically instead
 * of by scraping markup.
 *
 * ===========================================================================
 * WHY THE CHARTS ARE COMPUTED RATHER THAN DRAWN
 * ===========================================================================
 *
 * A training chart is the only instrument the player has. If it lies — an axis
 * that silently clips an outlier, a downsample that drops the last point so the
 * line never reaches the right edge, a NaN that collapses the whole path — the
 * failure is invisible, because a wrong chart looks exactly like a right one.
 * So the geometry is separated from the painting and pinned down by tests:
 * every range is finite and strictly non-empty, every downsample keeps the
 * first and last iteration, and an empty or one-point history produces a
 * drawable box rather than a division by zero.
 *
 * ===========================================================================
 * WHY IMPORT VALIDATION IS PARANOID
 * ===========================================================================
 *
 * A checkpoint arrives from a file picker, which means it arrives from
 * anywhere: a half-written CLI dump, a run of a different observation layout, a
 * JSON file that is not a checkpoint at all. Loading one of those does not
 * crash — it produces a policy that looks trained and plays like noise, which
 * is the same failure `Checkpoint.version` exists to prevent. `validateCheckpoint`
 * therefore checks the parameter vector's LENGTH against the shape the file
 * itself declares, which is the only check that catches a truncated write, and
 * returns a sentence a player can act on rather than a boolean.
 */

import { ACTION_COUNT } from '../rl/actions';
import {
  CHECKPOINT_VERSION,
  OBS_SIZE,
  type Checkpoint,
  type IterationStats,
  type TrainerPhase,
  type TrainingConfig,
  type TrainingStats,
} from '../rl/types';
import { DEFAULT_PPO_CONFIG } from '../rl/ppo';
import { DEFAULT_REWARD_WEIGHTS } from '../rl/reward';
import type { TrainingSettings } from '../game/settings';

// ---------------------------------------------------------------------------
// The run, as the UI sees it
// ---------------------------------------------------------------------------

/**
 * Network shape for a run started in the browser.
 *
 * MUST equal `DEFAULT_TRAINING_CONFIG.hiddenSizes` in src/rl/trainer.ts, and is
 * not imported from there because that module pulls in the whole simulation and
 * this one is on the menu model's import path. The equality is asserted in
 * test/ui/training.test.ts instead — the same trade the rest of this project
 * makes when a comment would otherwise be the only thing holding two constants
 * together. It matters because a checkpoint trained at one shape and a trainer
 * configured at another do not blend, they throw.
 */
export const TRAINING_HIDDEN_SIZES: readonly number[] = [256, 256];

/** Master seed for a browser run. Constant for the same reason `MATCH_SEED` is:
 *  a run described in a bug report has to be reproducible. */
export const TRAINING_SEED = 0x52574C52;

/** `TrainerPhase`s during which rollouts are being collected or applied. */
export const isTrainingActive = (phase: TrainerPhase): boolean =>
  phase === 'collecting' || phase === 'updating';

/** True when a run exists to be stopped — running or merely suspended. */
export const isTrainingLive = (phase: TrainerPhase): boolean =>
  isTrainingActive(phase) || phase === 'paused';

/** What the one run/pause/resume row reads, given the phase. */
export const runRowLabel = (phase: TrainerPhase): string => {
  if (isTrainingActive(phase)) return 'Pause Training';
  if (phase === 'paused') return 'Resume Training';
  return 'Start Training';
};

/**
 * The `TrainingConfig` the settings rows imply.
 *
 * `rewardWeights` is the defaults verbatim rather than a second copy: the
 * weights are tuned against each other (see src/rl/reward.ts) and a UI that
 * offered them individually would be offering a way to break the reward
 * function, not a way to tune it.
 */
export const trainingConfigFrom = (
  settings: TrainingSettings,
  seed: number = TRAINING_SEED
): TrainingConfig => ({
  ppo: {
    ...DEFAULT_PPO_CONFIG,
    learningRate: settings.learningRate,
    entropyCoef: settings.entropyCoef,
  },
  hiddenSizes: [...TRAINING_HIDDEN_SIZES],
  stepsPerIteration: settings.stepsPerIteration,
  workers: settings.workers,
  episodeTicks: settings.episodeTicks,
  seed,
  rewardWeights: { ...DEFAULT_REWARD_WEIGHTS },
});

/** An all-zero `TrainingStats`, so the panel renders before a run has begun. */
export const emptyTrainingStats = (): TrainingStats => ({
  iteration: 0,
  totalSteps: 0,
  totalSeconds: 0,
  history: [],
});

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/**
 * A step count, abbreviated. Training runs pass a million steps in minutes and
 * "1,284,032" in a fixed-width cell is a number nobody reads — the magnitude is
 * the whole of the information.
 */
export const formatCount = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  const n = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (n >= 1e9) return `${sign}${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${sign}${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e4) return `${sign}${(n / 1e3).toFixed(1)}k`;
  return `${sign}${Math.round(n)}`;
};

/** `h:mm:ss`, or `m:ss` under an hour. Elapsed training time, not a clock. */
export const formatElapsed = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const mm = m.toString().padStart(2, '0');
  const ss = s.toString().padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
};

/** A metric, with enough precision to see it move and no more. */
export const formatMetric = (value: number | null, decimals: number): string => {
  if (value === null || !Number.isFinite(value)) return '—';
  return value.toFixed(decimals);
};

/** A date on a checkpoint row. Local, short, and sortable by eye. */
export const formatSavedAt = (millis: number): string => {
  if (!Number.isFinite(millis) || millis <= 0) return '—';
  const d = new Date(millis);
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

export interface ChartPoint {
  /** Iteration number. */
  x: number;
  y: number;
}

export interface SeriesSpec {
  key: string;
  label: string;
  read: (stats: IterationStats) => number;
}

export interface ChartSpec {
  key: string;
  label: string;
  series: readonly SeriesSpec[];
  /** Pin zero onto the axis. True for rates and counts, where a range that
   *  floats off the baseline exaggerates noise into a trend. */
  includeZero: boolean;
  decimals: number;
  /** Render the readout as a percentage of 1. */
  percent?: boolean;
}

/**
 * The five charts the header of this project asks for, plus goals split into
 * for and against — a single "goals" line hides the case that matters, which is
 * a policy that has learned to score and to concede in equal measure.
 */
export const CHART_SPECS: readonly ChartSpec[] = [
  {
    key: 'return',
    label: 'Mean Return',
    series: [{ key: 'meanReturn', label: 'return', read: s => s.meanReturn }],
    includeZero: false,
    decimals: 2,
  },
  {
    key: 'touch',
    label: 'Touch Rate',
    series: [
      { key: 'touchRate', label: 'per agent', read: s => s.touchRate },
      // The curriculum's gate metric, on the same axes as the per-agent rate it
      // is easily mistaken for. Under self-play the two are far apart — the
      // per-agent one cannot exceed ~1/nLearners — and seeing that gap is what
      // stops someone reading a flat difficulty as a policy that is not good
      // enough yet. NaN for history written before it existed; `axisRange`
      // drops non-finite samples rather than letting one poison the axis.
      { key: 'episodeTouchRate', label: 'per episode', read: s => s.episodeTouchRate ?? Number.NaN },
    ],
    includeZero: true,
    decimals: 2,
    percent: true,
  },
  {
    key: 'goals',
    label: 'Goals / Episode',
    series: [
      { key: 'goalsFor', label: 'for', read: s => s.goalsFor },
      { key: 'goalsAgainst', label: 'against', read: s => s.goalsAgainst },
    ],
    includeZero: true,
    decimals: 3,
  },
  {
    key: 'entropy',
    label: 'Policy Entropy',
    series: [{ key: 'entropy', label: 'entropy', read: s => s.entropy }],
    includeZero: true,
    decimals: 3,
  },
  {
    key: 'kl',
    label: 'Approx KL',
    series: [
      { key: 'approxKl', label: 'kl', read: s => s.approxKl },
      { key: 'clipFraction', label: 'clip', read: s => s.clipFraction },
    ],
    includeZero: true,
    decimals: 4,
  },
];

/** How many points a chart line may carry. Beyond this the polyline costs more
 *  than one screen pixel per point and nothing is gained by drawing it. */
export const CHART_MAX_POINTS = 120;

export interface Range {
  min: number;
  max: number;
}

/**
 * A finite, strictly non-empty range covering `values`.
 *
 * The two degenerate cases are the ones that matter, because they are the ones
 * that happen first: no history at all, and one point. A zero-width range
 * divides by zero in every plotting expression downstream, so both are widened
 * here rather than guarded against in five different places.
 */
export const axisRange = (
  values: readonly number[],
  includeZero: boolean,
  /** Headroom added to each end, as a fraction of the span. Zero for the x
   *  axis: iterations 1..N should span the box exactly, and a line that stops
   *  short of the right edge reads as a run that has stalled. */
  padFraction = 0.06
): Range => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const v of values) {
    // Non-finite samples are dropped, not clamped: a single NaN in the history
    // would otherwise poison the axis for every other point.
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };

  if (includeZero) {
    min = Math.min(min, 0);
    max = Math.max(max, 0);
  }

  if (max - min < 1e-12) {
    // One distinct value. Widen by a tenth of its own magnitude so the line
    // sits in the middle of a box rather than on its edge.
    const pad = Math.max(Math.abs(max) * 0.1, 0.5);
    return { min: min - pad, max: max + pad };
  }

  const pad = (max - min) * padFraction;
  return { min: min - pad, max: max + pad };
};

/**
 * Reduce a series to at most `maxPoints` by averaging consecutive buckets.
 *
 * Averaging rather than striding, because striding a noisy series shows the
 * noise and hides the trend — which is the opposite of what the chart is for.
 * The FIRST and LAST raw points survive verbatim: a chart whose line stops
 * short of the right edge reads as "training stalled", and one that starts at
 * iteration 40 reads as "the first 40 iterations are missing".
 */
export const downsample = (
  points: readonly ChartPoint[],
  maxPoints: number = CHART_MAX_POINTS
): ChartPoint[] => {
  const limit = Math.max(2, Math.floor(maxPoints));
  if (points.length <= limit) return points.map(p => ({ x: p.x, y: p.y }));

  const out: ChartPoint[] = [{ x: points[0].x, y: points[0].y }];
  // Buckets cover everything after the retained first point; each contributes
  // one averaged sample whose x is its last member's, so x stays increasing.
  const rest = points.length - 1;
  const buckets = limit - 1;
  for (let b = 0; b < buckets; b++) {
    const start = 1 + Math.floor((b * rest) / buckets);
    const end = 1 + Math.floor(((b + 1) * rest) / buckets);
    if (end <= start) continue;
    let sum = 0;
    let count = 0;
    for (let i = start; i < end; i++) {
      if (!Number.isFinite(points[i].y)) continue;
      sum += points[i].y;
      count++;
    }
    out.push({ x: points[end - 1].x, y: count > 0 ? sum / count : points[end - 1].y });
  }
  return out;
};

export interface ChartLine {
  key: string;
  label: string;
  points: readonly ChartPoint[];
  /** Most recent raw value, before any downsampling. Null on empty history. */
  last: number | null;
}

export interface ChartView {
  key: string;
  label: string;
  lines: readonly ChartLine[];
  x: Range;
  y: Range;
  /** True when there is nothing to draw. The view still gets a valid box. */
  empty: boolean;
  decimals: number;
  percent: boolean;
}

/**
 * Everything needed to paint one chart. Total: any history at all, including
 * none, produces a `ChartView` whose every number is finite.
 */
export const buildChart = (
  spec: ChartSpec,
  history: readonly IterationStats[],
  maxPoints: number = CHART_MAX_POINTS
): ChartView => {
  const lines: ChartLine[] = [];
  const ys: number[] = [];
  const xs: number[] = [];

  for (const series of spec.series) {
    const raw: ChartPoint[] = [];
    for (const stats of history) {
      const y = series.read(stats);
      raw.push({ x: stats.iteration, y });
      if (Number.isFinite(y)) ys.push(y);
    }
    if (raw.length > 0) {
      xs.push(raw[0].x, raw[raw.length - 1].x);
    }
    const lastRaw = raw.length > 0 ? raw[raw.length - 1].y : null;
    lines.push({
      key: series.key,
      label: series.label,
      points: downsample(raw, maxPoints),
      last: lastRaw !== null && Number.isFinite(lastRaw) ? lastRaw : null,
    });
  }

  return {
    key: spec.key,
    label: spec.label,
    lines,
    // The x axis is the iteration range and takes no padding, so the line runs
    // edge to edge. A single-point history is still widened, which is what puts
    // that point in the middle of the box rather than on its left border.
    x: axisRange(xs, false, 0),
    y: axisRange(ys, spec.includeZero),
    empty: history.length === 0,
    decimals: spec.decimals,
    percent: spec.percent === true,
  };
};

export interface PlotBox {
  width: number;
  height: number;
  /** Inset on every side, so a line at the extreme of its range is not clipped
   *  in half by the viewBox edge. */
  pad: number;
}

/** Where a data point lands inside a box, in SVG user units. */
export const project = (point: ChartPoint, view: ChartView, box: PlotBox): ChartPoint => {
  const w = Math.max(1, box.width - box.pad * 2);
  const h = Math.max(1, box.height - box.pad * 2);
  const xSpan = view.x.max - view.x.min;
  const ySpan = view.y.max - view.y.min;
  const tx = xSpan > 0 ? (point.x - view.x.min) / xSpan : 0.5;
  const ty = ySpan > 0 ? (point.y - view.y.min) / ySpan : 0.5;
  const clamp01 = (t: number): number => (Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : 0.5);
  return {
    x: box.pad + clamp01(tx) * w,
    // SVG y grows downwards; a chart's does not.
    y: box.pad + (1 - clamp01(ty)) * h,
  };
};

/**
 * An SVG path for one line, or the empty string when there is nothing to draw.
 * Coordinates are rounded to two decimals: the extra digits are below a device
 * pixel and they triple the size of the markup being diffed into the DOM every
 * iteration.
 */
export const linePath = (line: ChartLine, view: ChartView, box: PlotBox): string => {
  const parts: string[] = [];
  for (const point of line.points) {
    if (!Number.isFinite(point.y)) continue;
    const p = project(point, view, box);
    parts.push(`${parts.length === 0 ? 'M' : 'L'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`);
  }
  // A single point has no length and paints nothing; give it a hairline so the
  // first iteration of a run is visible instead of looking like a failure.
  if (parts.length === 1) {
    const p = project(line.points[0], view, box);
    parts.push(`L${(p.x + 0.6).toFixed(2)} ${p.y.toFixed(2)}`);
  }
  return parts.join(' ');
};

// ---------------------------------------------------------------------------
// Reward breakdown
// ---------------------------------------------------------------------------

export interface RewardBar {
  name: string;
  value: number;
  /** 0..1 share of the largest magnitude in the set, for the bar width. */
  fraction: number;
  negative: boolean;
}

/**
 * The per-term breakdown, largest contribution first.
 *
 * Scaled by the largest MAGNITUDE rather than by the sum: the terms have
 * opposite signs and a sum near zero — which is exactly the interesting case,
 * a policy earning as much as it loses — would blow every bar off the chart.
 */
export const rewardBars = (terms: Record<string, number> | undefined | null): RewardBar[] => {
  if (!terms || typeof terms !== 'object') return [];
  const entries: RewardBar[] = [];
  let peak = 0;
  for (const [name, raw] of Object.entries(terms)) {
    const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
    peak = Math.max(peak, Math.abs(value));
    entries.push({ name, value, fraction: 0, negative: value < 0 });
  }
  for (const entry of entries) {
    entry.fraction = peak > 0 ? Math.abs(entry.value) / peak : 0;
  }
  entries.sort((a, b) => Math.abs(b.value) - Math.abs(a.value) || a.name.localeCompare(b.name));
  return entries;
};

// ---------------------------------------------------------------------------
// Checkpoint files
// ---------------------------------------------------------------------------

/**
 * Parameter count for a network of this shape. The same walk `MlpPolicy`'s
 * constructor does, and it has to agree with it — which is why it is derived
 * from the layer sizes here rather than being read out of the file it is
 * checking. A truncated write is a file whose declared shape and actual
 * parameter vector disagree, and this is the only thing that notices.
 */
export const parameterCountFor = (
  obsSize: number,
  hiddenSizes: readonly number[],
  actionCount: number
): number => {
  let total = 0;
  let width = obsSize;
  for (const h of hiddenSizes) {
    total += h * width + h;
    width = h;
  }
  // Policy head, then value head.
  total += actionCount * width + actionCount;
  total += width + 1;
  return total;
};

export type CheckpointCheck =
  | { ok: true; checkpoint: Checkpoint }
  | { ok: false; message: string };

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isFiniteNumberArray = (v: unknown): v is number[] =>
  Array.isArray(v) && v.every(n => typeof n === 'number' && Number.isFinite(n));

const isPositiveIntArray = (v: unknown): v is number[] =>
  Array.isArray(v) &&
  v.length > 0 &&
  v.every(n => typeof n === 'number' && Number.isInteger(n) && n > 0);

/**
 * Validate a parsed checkpoint. Returns the value narrowed, or a sentence.
 *
 * Every message names the mismatch and both sides of it. "Incompatible
 * checkpoint" tells a player nothing they can act on; "trained on 64 inputs,
 * this build uses 70" tells them the file is from an older layout and is not
 * coming back.
 */
export const validateCheckpoint = (value: unknown): CheckpointCheck => {
  if (!isRecord(value)) return { ok: false, message: 'That file does not contain a checkpoint.' };

  if (value.version !== CHECKPOINT_VERSION) {
    return {
      ok: false,
      message:
        typeof value.version === 'number'
          ? `Checkpoint format v${value.version}; this build reads v${CHECKPOINT_VERSION}.`
          : 'That file has no checkpoint version and cannot be trusted.',
    };
  }
  if (value.obsSize !== OBS_SIZE) {
    return {
      ok: false,
      message: `Trained on ${String(value.obsSize)} observation inputs; this build uses ${OBS_SIZE}.`,
    };
  }
  if (value.actionCount !== ACTION_COUNT) {
    return {
      ok: false,
      message: `Trained on ${String(value.actionCount)} actions; this build has ${ACTION_COUNT}.`,
    };
  }
  if (!isPositiveIntArray(value.hiddenSizes)) {
    return { ok: false, message: 'The checkpoint does not say what shape its network is.' };
  }
  if (!isFiniteNumberArray(value.parameters)) {
    return { ok: false, message: 'The checkpoint’s weights are missing or not numbers.' };
  }

  const expected = parameterCountFor(OBS_SIZE, value.hiddenSizes, ACTION_COUNT);
  if (value.parameters.length !== expected) {
    return {
      ok: false,
      message: `The file holds ${value.parameters.length} weights; this shape needs ${expected}. It is truncated or corrupt.`,
    };
  }

  return { ok: true, checkpoint: value as unknown as Checkpoint };
};

/** Parse and validate a checkpoint file's text. Never throws. */
export const parseCheckpointFile = (text: string): CheckpointCheck => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, message: 'That file is not JSON.' };
  }
  return validateCheckpoint(parsed);
};

/**
 * One stored checkpoint, as the MENU needs it.
 *
 * Deliberately not `CheckpointMeta` from src/rl/storage.ts, even though the
 * fields line up today: the menu model is pure and must stay importable in Node
 * with no store behind it, and the view is the only thing that should know how
 * a checkpoint is persisted. The mapping is one object literal in
 * src/ui/training.ts.
 */
export interface CheckpointRow {
  id: string;
  label: string;
  iteration: number;
  totalSteps: number;
  savedAt: number;
}

/** The one-line summary a checkpoint row reads as. */
export const checkpointRowLabel = (row: CheckpointRow): string =>
  `${row.label} · i${row.iteration} · ${formatCount(row.totalSteps)} · ${formatSavedAt(row.savedAt)}`;

/** A checkpoint as a downloadable file's contents. */
export const serializeCheckpoint = (checkpoint: Checkpoint): string => JSON.stringify(checkpoint);

/** A filename that sorts by run and says what is in it without being opened. */
export const checkpointFilename = (checkpoint: Checkpoint): string => {
  const iteration = Number.isFinite(checkpoint.stats?.iteration)
    ? checkpoint.stats.iteration
    : 0;
  const steps = Number.isFinite(checkpoint.stats?.totalSteps) ? checkpoint.stats.totalSteps : 0;
  return `vroomball-policy-i${iteration}-${formatCount(steps)}.json`;
};
